import React, { Fragment, useState, useCallback } from 'react'
import axios from 'axios'
import {
  object, string, bool, func, number,
} from 'prop-types'
import { TrendingUpIcon, Button, Icon as SSRIcon } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import Icon from 'Pods/Shared/Icon/Icon'

import PromoteModel from './Components/PromoteModel/PromoteModel'

function TableActions({
  model,
  status,
  allowToDeleteModels,
  onModelDelete,
  editCollection,
  isAddingToCollection,
  downloadData = null,
  isDownloadMyModelsEnabled = false,
  isSalesBoostEnabled = false,
  salesBoostPoints = 0,
  limitTagCampaignsAndCostInflation = false,
  onCampaignSubmitted,
}) {
  const [promoteModalIsOpen, setPromoteModalIsOpen] = useState(false)
  const [submittingCampaign, setSubmittingCampaign] = useState(false)

  const { attributes, type, id } = model || {}
  const {
    price,
    totalSales,
    status: itemStatus,
    promotionStatus,
    title,
    primaryImage,
  } = attributes || {}
  const { thumbUrl } = primaryImage || {}

  const isCollection = type === 'collections'
  const isDraft = status === 'draft'
  const isSuspended = status === 'suspended'
  const isRestricted = status === 'restricted'

  const forbiddenErr = () => {
    App.events.publish('app:notification:flash:error', 'Forbidden')
  }

  const openPromoteModal = useCallback(() => {
    setPromoteModalIsOpen(true)
  }, [])

  const closePromoteModal = useCallback(() => {
    setPromoteModalIsOpen(false)
  }, [])

  const handleStartCampaign = useCallback(async (campaignData) => {
    const genericErrorMessage = 'Failed to submit your campaign for moderation.'

    setSubmittingCampaign(true)
    try {
      const { selectedTags, duration } = campaignData

      const payload = {
        duration,
        tags: selectedTags.map(tagObj => tagObj.tag)
      }

      const { data } = await axios.post(`/api/internal/items/${id}/update_promotion`, payload)
      closePromoteModal()

      if (data.success === true) {
        App.events.publish('update-boost-credits', data.salesBoostPoints)
        App.events.publish('app:notification:flash:success', 'Your campaign has been submitted for moderation.')
      } else {
        App.events.publish('app:notification:flash:error', data.error || genericErrorMessage)
      }

      onCampaignSubmitted()
    } catch (error) {
      const { response } = error ?? {}
      const { data, status: errorStatus } = response ?? {}
      const { error: errorMessage } = data ?? {}
      App.events.publish('app:notification:flash:error', errorMessage || genericErrorMessage)

      if (errorStatus !== 422) {
        window.Sentry.captureException(error, {
          tags: {
            component: 'TableActions',
            action: 'handleStartCampaign',
            itemId: id,
          },
          extra: {
            itemId: id,
            itemTitle: title,
            payload: {
              duration: campaignData.duration,
              tags: campaignData.selectedTags.map(tagObj => tagObj.tag),
              amount: campaignData.totalCost,
            },
            errorMessage: error.message,
            errorResponse: error.response?.data,
          },
        })
      }
    } finally {
      setSubmittingCampaign(false)
    }
  }, [closePromoteModal, id, title, onCampaignSubmitted])

  const btnStyle = isDraft ? 'btn-primary' : 'btn-outline-default btn-white'
  const btnIconColor = isDraft ? 'white' : 'grey-dark'

  const renderButton = () => {
    if (isSuspended) {
      return (
        <button
          className={`btn btn-icon-round ${btnStyle}`}
          onClick={() => { forbiddenErr() }}
        >
          <Icon type={['far', 'edit']} color={btnIconColor} notSpaced />
        </button>
      )
    }

    if (isCollection) {
      return (
        <button
          className={`btn btn-icon-round ${btnStyle}`}
          onClick={() => editCollection(id)}
        >
          <Icon type={['far', 'edit']} color={btnIconColor} notSpaced />
        </button>
      )
    }

    return (
      <a
        href={`/profile/models/${id}/edit`}
        target="_blank"
        rel="noreferrer"
        className={`btn btn-icon-round ${btnStyle}`}
      >
        <Icon type={['far', 'edit']} color={btnIconColor} notSpaced />
      </a>
    )
  }

  return (
    <>
      <div className="u-text-center">
        {isAddingToCollection ? (
          <button
            className={`btn btn-icon-round ${btnStyle}`}
            disabled={isSuspended}
            onClick={() => editCollection(id)}
          >
            <Icon type={['fas', 'plus']} color={btnIconColor} notSpaced />
          </button>
        ) : (
          <Fragment>
            {(allowToDeleteModels || isDraft || isSuspended || isRestricted || isCollection) && (
              <button
                className="btn btn-outline-default btn-white btn-icon-round u-mr10"
                onClick={() => onModelDelete(model)}
              >
                <Icon type={['far', 'trash']} color="grey-dark" notSpaced />
              </button>
            )}
            {renderButton()}
          </Fragment>
        )}
        {downloadData && isDownloadMyModelsEnabled && (
          <button
            data-href={downloadData.href}
            data-toggle={downloadData.toggle}
            data-target={downloadData.target}
            className="btn btn-outline-default btn-icon-round u-ml10 js-download"
          >
            <Icon type={['far', 'download']} color="grey-dark" notSpaced />
          </button>
        )}
      </div>
      {
        !isAddingToCollection
        && !isSuspended
        && isSalesBoostEnabled
        && price > 0
        && itemStatus === 'published'
        && (
          <>
            <div className="u-mt10">
              <Button
                trailIcon={<SSRIcon svgComponent={TrendingUpIcon} />}
                size="small"
                type="secondary"
                variant="with-icon"
                onClick={openPromoteModal}
              >
                Promote
              </Button>
            </div>
            {promoteModalIsOpen && (
              <PromoteModel
                closeModal={closePromoteModal}
                onStartCampaign={handleStartCampaign}
                itemTitle={title}
                itemId={id}
                itemImage={thumbUrl}
                itemPrice={price}
                itemSales={totalSales}
                salesBoostPoints={salesBoostPoints}
                limitTagCampaignsAndCostInflation={limitTagCampaignsAndCostInflation}
                isReadOnly={['pending', 'running', 'approved', 'declined', 'paused'].includes(promotionStatus)}
                promotionStatus={promotionStatus}
                submittingCampaign={submittingCampaign}
              />
            )}
          </>
        )
      }
    </>
  )
}

TableActions.propTypes = {
  model: object.isRequired,
  status: string.isRequired,
  allowToDeleteModels: bool.isRequired,
  onModelDelete: func.isRequired,
  editCollection: func.isRequired,
  isAddingToCollection: bool.isRequired,
  downloadData: object,
  isDownloadMyModelsEnabled: bool,
  isSalesBoostEnabled: bool,
  salesBoostPoints: number,
  limitTagCampaignsAndCostInflation: bool,
  onCampaignSubmitted: func.isRequired,
}

export default TableActions
